import { MultiSelect } from '@/components/MultiSelect';
import { useHistorico } from '@/hooks/useReportes';
import { cn } from '@/lib/utils';
import type { HistoricoReporte, HistoricoSub } from '@adelante/shared';
import { CircleNotch, MagnifyingGlass } from '@phosphor-icons/react';
import { type CSSProperties, useMemo, useState } from 'react';

type Vista = 'sprint' | 'partida' | 'kanban';

/**
 * Estilo/contenido de una celda. `avance` = tuvo avance esta semana → raya
 * vertical azul. ✓ verde solo si se COMPLETÓ esta semana; "100" (verde claro)
 * si ya venía completa. 1-99 = mapa de calor ámbar. 0 gris, NA (no aplica) tenue.
 */
function celdaInfo(v: number | undefined, avance: boolean): { txt: string; style: CSSProperties } {
  const linea: CSSProperties = avance ? { boxShadow: 'inset 3px 0 0 #2563eb' } : {};
  if (v === undefined) return { txt: 'NA', style: { background: '#fafafa', color: '#d4d4d8' } };
  if (v >= 100) {
    return avance
      ? { txt: '✓', style: { background: 'rgba(34,197,94,0.5)', color: '#14532d', ...linea } }
      : { txt: '100', style: { background: 'rgba(187,247,208,0.55)', color: '#166534' } };
  }
  if (v <= 0) return { txt: '0', style: { background: '#f4f4f5', color: '#a1a1aa', ...linea } };
  return {
    txt: String(Math.round(v)),
    style: { background: `rgba(245,158,11,${0.18 + (v / 100) * 0.6})`, color: '#451a03', ...linea },
  };
}

/**
 * Vistas históricas (foto del cierre de una semana): Por Sprint / Por Partida
 * (grilla sub-partida × obra) y Kanban (tarjetas por sprint). Read-only.
 */
export function HistoricoVista({ semana }: { semana: number | null }) {
  const { data, isLoading, isError } = useHistorico(semana);
  const h = data?.data;
  const [vista, setVista] = useState<Vista>('sprint');
  const [busqueda, setBusqueda] = useState('');
  const [partidaSel, setPartidaSel] = useState<string[]>([]);
  const [subSel, setSubSel] = useState<string[]>([]);
  const [soloAvance, setSoloAvance] = useState(false);

  // Partidas disponibles (para el filtro), en orden de código.
  const partidas = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of h?.subs ?? []) m.set(s.partida_id, `${s.partida_codigo} — ${s.partida_nombre}`);
    return [...m.entries()]
      .sort((a, b) => a[1].localeCompare(b[1], 'es', { numeric: true }))
      .map(([id, label]) => ({ value: String(id), label }));
  }, [h]);
  // Sub-partidas ofrecidas: las de la partida elegida (o todas).
  const subsOpciones = useMemo(
    () =>
      (h?.subs ?? [])
        .filter((s) => partidaSel.length === 0 || partidaSel.includes(String(s.partida_id)))
        .map((s) => ({ value: String(s.id), label: `${s.partida_codigo} · ${s.nombre}` })),
    [h, partidaSel],
  );

  // Obras (columnas) y sub-partidas (filas) tras búsqueda + filtros. "Solo con
  // avance" deja las obras con avance esta semana y las subs que avanzaron.
  const { obras, subs } = useMemo(() => {
    if (!h) return { obras: [], subs: [] };
    const q = busqueda.trim().toLowerCase();
    let obrasF = q ? h.obras.filter((o) => o.codigo.toLowerCase().includes(q)) : h.obras;
    let subsF = h.subs.filter(
      (s) =>
        (partidaSel.length === 0 || partidaSel.includes(String(s.partida_id))) &&
        (subSel.length === 0 || subSel.includes(String(s.id))),
    );
    if (soloAvance) {
      const av = new Set(h.avanceSemana);
      obrasF = obrasF.filter((o) => subsF.some((s) => av.has(`${o.codigo}|${s.id}`)));
      subsF = subsF.filter((s) => obrasF.some((o) => av.has(`${o.codigo}|${s.id}`)));
    }
    return { obras: obrasF, subs: subsF };
  }, [h, busqueda, partidaSel, subSel, soloAvance]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
        Cargando histórico…
      </div>
    );
  }
  if (isError || !h) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No se pudo cargar el histórico.
      </p>
    );
  }

  const hVista: HistoricoReporte = { ...h, subs };
  const celda = (obra: string, sub: number): number | undefined => h.celdas[`${obra}|${sub}`];
  const avanceSet = new Set(h.avanceSemana);
  const avanzo = (obra: string, sub: number) => avanceSet.has(`${obra}|${sub}`);

  return (
    <div className="relative left-1/2 w-screen -translate-x-1/2 px-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {/* Toggle de vista */}
        <div className="inline-flex rounded-md border border-border p-0.5">
          {(
            [
              ['sprint', 'Por Sprint'],
              ['partida', 'Por Partida'],
              ['kanban', 'Kanban'],
            ] as [Vista, string][]
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setVista(v)}
              className={cn(
                'rounded px-3 py-1 text-xs font-medium transition-colors',
                vista === v
                  ? 'bg-adelante-primary text-adelante-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        {/* Buscador */}
        <div className="relative">
          <MagnifyingGlass
            className="-translate-y-1/2 absolute top-1/2 left-2 h-4 w-4 text-muted-foreground"
            weight="bold"
            aria-hidden="true"
          />
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar obra…"
            className="h-8 w-56 rounded-md border border-border pr-2 pl-8 text-sm"
          />
        </div>
        {/* Filtros (selección múltiple) de partida / sub-partida */}
        <MultiSelect
          opciones={partidas}
          seleccion={partidaSel}
          onChange={(v) => {
            setPartidaSel(v);
            // Descarta sub-partidas que ya no pertenecen a las partidas elegidas.
            const ok = new Set(
              (h?.subs ?? [])
                .filter((x) => v.length === 0 || v.includes(String(x.partida_id)))
                .map((x) => String(x.id)),
            );
            setSubSel((prev) => prev.filter((id) => ok.has(id)));
          }}
          todasLabel="Todas las partidas"
        />
        <MultiSelect
          opciones={subsOpciones}
          seleccion={subSel}
          onChange={setSubSel}
          todasLabel="Todas las sub-partidas"
        />
        <label className="flex cursor-pointer items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={soloAvance}
            onChange={(e) => setSoloAvance(e.target.checked)}
          />
          Solo con avance en la semana
        </label>
        <span className="text-xs text-muted-foreground">
          {obras.length} obra{obras.length === 1 ? '' : 's'} ·{' '}
          {h.cerrada ? 'foto del cierre' : 'estado vivo (semana abierta)'} · Semana{' '}
          {h.semana.numero_semana}:{' '}
          {h.semana.fecha_inicio.slice(0, 10).split('-').reverse().join('/')} →{' '}
          {h.semana.fecha_fin.slice(0, 10).split('-').reverse().join('/')}
        </span>
      </div>

      {soloAvance && obras.length === 0 && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          Ninguna obra tiene avance en esta semana con los filtros actuales
          {h.cerrada
            ? '.'
            : ' (la semana está abierta: el avance aparece a medida que se registra).'}
        </p>
      )}

      {vista === 'kanban' ? (
        <HistoricoKanban h={hVista} obras={obras} celda={celda} avanzo={avanzo} />
      ) : (
        <HistoricoGrilla h={hVista} obras={obras} celda={celda} avanzo={avanzo} agrupar={vista} />
      )}
    </div>
  );
}

// ---- Grilla (Por Sprint / Por Partida): sub-partidas (filas) × obras (cols) ----
function HistoricoGrilla({
  h,
  obras,
  celda,
  avanzo,
  agrupar,
}: {
  h: HistoricoReporte;
  obras: HistoricoReporte['obras'];
  celda: (o: string, s: number) => number | undefined;
  avanzo: (o: string, s: number) => boolean;
  agrupar: 'sprint' | 'partida';
}) {
  // Agrupar las sub-partidas por sprint o por partida, preservando el orden.
  const grupos = useMemo(() => {
    const out: { clave: string; label: string; subs: HistoricoSub[] }[] = [];
    const idx = new Map<string, number>();
    const subsOrden =
      agrupar === 'partida'
        ? [...h.subs].sort(
            (a, b) =>
              a.partida_codigo.localeCompare(b.partida_codigo, 'es', { numeric: true }) ||
              a.codigo.localeCompare(b.codigo, 'es', { numeric: true }),
          )
        : h.subs; // ya viene ordenado por sprint, codigo
    for (const s of subsOrden) {
      const clave = agrupar === 'sprint' ? `s${s.sprint_numero}` : `p${s.partida_id}`;
      const label =
        agrupar === 'sprint'
          ? `Sprint ${s.sprint_numero}`
          : `${s.partida_codigo} — ${s.partida_nombre}`;
      let i = idx.get(clave);
      if (i === undefined) {
        i = out.length;
        idx.set(clave, i);
        out.push({ clave, label, subs: [] });
      }
      out[i]!.subs.push(s);
    }
    return out;
  }, [h.subs, agrupar]);

  const thBase = 'h-7 bg-zinc-100 px-1 text-[11px] font-medium text-muted-foreground';
  const totalW = 220 + obras.length * 52;

  return (
    <div
      className="overflow-auto rounded-md border border-border"
      style={{ maxHeight: 'calc(100vh - 220px)' }}
    >
      <table className="table-fixed border-collapse" style={{ width: totalW }}>
        <colgroup>
          <col style={{ width: 220 }} />
          {obras.map((o) => (
            <col key={o.codigo} style={{ width: 52 }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className={cn(thBase, 'sticky top-0 left-0 z-30 border-b border-border text-left')}>
              Sub-partida
            </th>
            {obras.map((o) => (
              <th
                key={o.codigo}
                className={cn(
                  thBase,
                  'sticky top-0 z-20 overflow-hidden border-b border-l border-border',
                )}
                title={`${o.codigo} · ${o.tipo_casa ?? ''} · S${o.sprint_actual ?? '-'}`}
              >
                <span className="block truncate font-mono text-[10px]">{o.codigo}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grupos.map((g) => (
            <GrupoFilas key={g.clave} g={g} obras={obras} celda={celda} avanzo={avanzo} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GrupoFilas({
  g,
  obras,
  celda,
  avanzo,
}: {
  g: { clave: string; label: string; subs: HistoricoSub[] };
  obras: HistoricoReporte['obras'];
  celda: (o: string, s: number) => number | undefined;
  avanzo: (o: string, s: number) => boolean;
}) {
  return (
    <>
      <tr>
        <td
          colSpan={obras.length + 1}
          className="sticky left-0 border-y border-border bg-zinc-200/70 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-600"
        >
          {g.label}
        </td>
      </tr>
      {g.subs.map((s) => (
        <tr key={s.id}>
          <td className="sticky left-0 z-10 overflow-hidden truncate border-b border-border bg-white px-2 py-1 text-xs">
            {s.es_critica && <span className="mr-1 text-red-500">●</span>}
            {s.nombre}
          </td>
          {obras.map((o) => {
            const info = celdaInfo(celda(o.codigo, s.id), avanzo(o.codigo, s.id));
            return (
              <td
                key={o.codigo}
                className="border-b border-l border-border px-1 py-1 text-center text-xs tabular-nums"
                style={info.style}
              >
                {info.txt}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

// ---- Kanban histórico: columnas = sprint, tarjetas = obras en ese sprint ----
function HistoricoKanban({
  h,
  obras,
  celda,
  avanzo,
}: {
  h: HistoricoReporte;
  obras: HistoricoReporte['obras'];
  celda: (o: string, s: number) => number | undefined;
  avanzo: (o: string, s: number) => boolean;
}) {
  const subsPorSprint = useMemo(() => {
    const m = new Map<number, HistoricoSub[]>();
    for (const s of h.subs) {
      const arr = m.get(s.sprint_numero) ?? [];
      arr.push(s);
      m.set(s.sprint_numero, arr);
    }
    return m;
  }, [h.subs]);

  // Columnas = sprints presentes entre las obras (por su sprint_actual), ordenadas.
  const sprints = useMemo(() => {
    const set = new Set<number>();
    for (const o of obras) if (o.sprint_actual != null) set.add(o.sprint_actual);
    return [...set].sort((a, b) => a - b);
  }, [obras]);

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {sprints.map((spr) => {
        const obrasDelSprint = obras.filter((o) => o.sprint_actual === spr);
        const subsSprint = subsPorSprint.get(spr) ?? [];
        return (
          <div key={spr} className="w-64 shrink-0">
            <div className="mb-2 rounded-md bg-zinc-800 px-3 py-1.5 text-sm font-semibold text-white">
              Sprint {spr} · {obrasDelSprint.length} obras
            </div>
            <div className="space-y-2">
              {obrasDelSprint.map((o) => {
                const aplican = subsSprint.filter((s) => celda(o.codigo, s.id) !== undefined);
                const criticas = aplican.filter((s) => s.es_critica);
                const noCriticas = aplican.filter((s) => !s.es_critica);
                return (
                  <div key={o.codigo} className="rounded-md border border-border bg-white p-2">
                    <p className="mb-1 font-mono text-xs font-semibold">
                      {o.codigo} <span className="text-muted-foreground">· {o.tipo_casa}</span>
                    </p>
                    {criticas.length > 0 && (
                      <SubLista
                        titulo="Críticas"
                        subs={criticas}
                        obra={o.codigo}
                        celda={celda}
                        avanzo={avanzo}
                      />
                    )}
                    {noCriticas.length > 0 && (
                      <SubLista
                        titulo="No críticas"
                        subs={noCriticas}
                        obra={o.codigo}
                        celda={celda}
                        avanzo={avanzo}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SubLista({
  titulo,
  subs,
  obra,
  celda,
  avanzo,
}: {
  titulo: string;
  subs: HistoricoSub[];
  obra: string;
  celda: (o: string, s: number) => number | undefined;
  avanzo: (o: string, s: number) => boolean;
}) {
  return (
    <div className="mt-1">
      <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
        {titulo}
      </p>
      <ul className="text-[11px]">
        {subs.map((s) => {
          const v = celda(obra, s.id);
          const avance = avanzo(obra, s.id);
          const completadaSemana = v != null && v >= 100 && avance;
          const txt =
            v === undefined
              ? '—'
              : completadaSemana
                ? '✓'
                : v >= 100
                  ? '100%'
                  : `${Math.round(v)}%`;
          return (
            <li
              key={s.id}
              className={cn(
                'flex items-center justify-between gap-1 py-0.5',
                avance && 'border-blue-500 border-l-2 pl-1',
              )}
            >
              <span className="min-w-0 flex-1 truncate">{s.nombre}</span>
              <span
                className={cn(
                  'shrink-0 tabular-nums',
                  completadaSemana
                    ? 'font-semibold text-green-600'
                    : v != null && v >= 100
                      ? 'font-semibold text-emerald-700'
                      : 'text-muted-foreground',
                )}
              >
                {txt}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
